import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const DonationsManagement = () => {
  const { makeAuthenticatedRequest } = useAuth();
  const [donations, setDonations] = useState([]);
  const [fundraisers, setFundraisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDonation, setEditingDonation] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    fundraiser_id: '',
    amount: '',
    customer_name: '',
    customer_email: '',
    source: 'manual',
    notes: '',
    is_anonymous: false
  });

  useEffect(() => {
    fetchDonations();
    fetchFundraisers();
  }, []);

  const fetchDonations = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await makeAuthenticatedRequest(`${apiUrl}/donations`);
      if (response.ok) {
        const data = await response.json();
        setDonations(data);
      } else {
        setError('Failed to load donations');
      }
    } catch (err) {
      setError('Error loading donations: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchFundraisers = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/fundraisers`);
      if (response.ok) {
        const data = await response.json();
        setFundraisers(data);
      }
    } catch (err) {
      console.error('Error loading fundraisers:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.fundraiser_id || !formData.amount || formData.amount <= 0) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const url = editingDonation
        ? `${apiUrl}/donations/${editingDonation.id}`
        : `${apiUrl}/donations`;

      const response = await makeAuthenticatedRequest(url, {
        method: editingDonation ? 'PUT' : 'POST',
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
          is_anonymous: formData.is_anonymous ? 1 : 0
        })
      });

      if (response.ok) {
        setSuccess(editingDonation ? 'Donation updated successfully!' : 'Donation added successfully!');
        setShowAddForm(false);
        setEditingDonation(null);
        resetForm();
        fetchDonations();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save donation');
      }
    } catch (err) {
      setError('Error saving donation: ' + err.message);
    }
  };

  const handleEdit = (donation) => {
    setEditingDonation(donation);
    setFormData({
      fundraiser_id: donation.fundraiser_id,
      amount: donation.amount.toString(),
      customer_name: donation.customer_name || '',
      customer_email: donation.customer_email || '',
      source: donation.source || 'manual',
      notes: donation.notes || '',
      is_anonymous: donation.is_anonymous === 1
    });
    setShowAddForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (donationId) => {
    if (!window.confirm('Are you sure you want to delete this donation? This action cannot be undone.')) {
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await makeAuthenticatedRequest(`${apiUrl}/donations/${donationId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Donation deleted successfully!');
        fetchDonations();
      } else {
        setError('Failed to delete donation');
      }
    } catch (err) {
      setError('Error deleting donation: ' + err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      fundraiser_id: '',
      amount: '',
      customer_name: '',
      customer_email: '',
      source: 'manual',
      notes: '',
      is_anonymous: false
    });
  };

  const cancelEdit = () => {
    setShowAddForm(false);
    setEditingDonation(null);
    resetForm();
    setError('');
    setSuccess('');
  };

  const getFundraiserName = (fundraiserId) => {
    const fundraiser = fundraisers.find(f => f.id === fundraiserId);
    return fundraiser ? fundraiser.name : 'Unknown';
  };

  const getTotalByFundraiser = () => {
    const totals = {};
    donations.forEach(donation => {
      if (!totals[donation.fundraiser_id]) {
        totals[donation.fundraiser_id] = 0;
      }
      totals[donation.fundraiser_id] += donation.amount;
    });
    return totals;
  };

  const totals = getTotalByFundraiser();

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="text-4xl mb-4">💰</div>
        <p className="text-gray-600">Loading donations...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Donations Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            View all donations and manually add offline donations
          </p>
        </div>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            if (showAddForm) cancelEdit();
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
        >
          {showAddForm ? 'Cancel' : '+ Add Donation'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-3 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 text-green-600 p-3 rounded-lg border border-green-200">
          {success}
        </div>
      )}

      {/* Add/Edit Donation Form */}
      {showAddForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            {editingDonation ? 'Edit Donation' : 'Add Manual Donation'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fundraiser *
                </label>
                <select
                  value={formData.fundraiser_id}
                  onChange={(e) => setFormData({...formData, fundraiser_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select fundraiser...</option>
                  {fundraisers.map(f => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Donor Name
                </label>
                <input
                  type="text"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Donor Email
                </label>
                <input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source
                </label>
                <select
                  value={formData.source}
                  onChange={(e) => setFormData({...formData, source: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="manual">Manual Entry</option>
                  <option value="check">Check</option>
                  <option value="venmo">Venmo</option>
                  <option value="cash">Cash</option>
                  <option value="gofundme">Gofundme</option>
                  <option value="zelle">Zelle</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer pt-7">
                  <input
                    type="checkbox"
                    checked={formData.is_anonymous}
                    onChange={(e) => setFormData({...formData, is_anonymous: e.target.checked})}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Anonymous donation
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Add any additional notes about this donation..."
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {editingDonation ? 'Update Donation' : 'Add Donation'}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Donations Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-blue-700">Total Donations</p>
            <p className="text-2xl font-bold text-blue-900">{donations.length}</p>
          </div>
          <div>
            <p className="text-blue-700">Total Amount</p>
            <p className="text-2xl font-bold text-blue-900">
              ${donations.reduce((sum, d) => sum + d.amount, 0).toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-blue-700">Online (Stripe)</p>
            <p className="text-2xl font-bold text-blue-900">
              {donations.filter(d => d.source === 'stripe').length}
            </p>
          </div>
          <div>
            <p className="text-blue-700">Manual</p>
            <p className="text-2xl font-bold text-blue-900">
              {donations.filter(d => d.source !== 'stripe').length}
            </p>
          </div>
        </div>
      </div>

      {/* Donations List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fundraiser
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Donor
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {donations.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    No donations found
                  </td>
                </tr>
              ) : (
                donations.map((donation) => (
                  <tr key={donation.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(donation.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {getFundraiserName(donation.fundraiser_id)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {donation.is_anonymous === 1 ? (
                        <span className="text-gray-500 italic">Anonymous</span>
                      ) : (
                        donation.customer_name || <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-600">
                      ${donation.amount.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        donation.source === 'stripe'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {donation.source}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {donation.notes || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-right space-x-2">
                      {donation.source !== 'stripe' && (
                        <>
                          <button
                            onClick={() => handleEdit(donation)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(donation.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {donation.source === 'stripe' && (
                        <span className="text-gray-400">Stripe only</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals by Fundraiser */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-800 mb-4">Totals by Fundraiser</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.keys(totals).map((fundraiserId) => (
            <div key={fundraiserId} className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">{getFundraiserName(parseInt(fundraiserId))}</p>
              <p className="text-xl font-bold text-gray-900">${totals[fundraiserId].toFixed(2)}</p>
              <p className="text-xs text-gray-500">
                {donations.filter(d => d.fundraiser_id === parseInt(fundraiserId)).length} donations
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DonationsManagement;
